import { Dispatch, SetStateAction } from 'react';
import { PRICING_TYPE_OPTIONS } from '../components/pricing-type-options.constants';

interface UseDateSelectionProps {
  setCalendarSelectedDatesByFreelancer: Dispatch<SetStateAction<Record<string, Date[]>>>;
  setTableRowsByFreelancer: Dispatch<SetStateAction<Record<string, any[]>>>;
  setShowCalendar: Dispatch<SetStateAction<boolean>>;
  lastUsedPricingTypeByFreelancer: Record<string, string>;
  isRegularEngagement: boolean;
  weekOffDays: string[];
  isRutOrRotDeduction: boolean;
  serviceDescription: string;
  setValue: (field: string, value: any) => void;
  handleInputChange: (field: string, value: any) => void;
  watch: (field: string) => any;
}

// Optional override values for freelancer-specific data
interface DateSelectOverrides {
  hoursPerDay?: string;
  hourlyPrice?: string;
  serviceDescription?: string;
  weekOffDays?: string[];
  pricingType?: string;
  // When true, newly selected dates are ADDED to the freelancer's existing rows
  // instead of replacing them (used by "Add Multiple Rows" so previously added
  // working hours are preserved until the user removes them explicitly).
  appendToExisting?: boolean;
}

export const useDateSelection = ({
  setCalendarSelectedDatesByFreelancer,
  setTableRowsByFreelancer,
  setShowCalendar,
  lastUsedPricingTypeByFreelancer,
  isRegularEngagement,
  weekOffDays,
  isRutOrRotDeduction,
  serviceDescription,
  setValue,
  handleInputChange,
  watch
}: UseDateSelectionProps) => {

  const handleDateSelect = (
    freelancerId: string, 
    startDate: Date, 
    endDate?: Date, 
    selectedDates?: Date[],
    overrides?: DateSelectOverrides
  ) => {
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    };

    // Helper function to check if a date is a week-off day
    const isWeekOffDay = (date: Date): boolean => {
      // Use override weekOffDays if provided, otherwise use prop weekOffDays
      const effectiveWeekOffDays = overrides?.weekOffDays ?? weekOffDays;
      
      if (!isRegularEngagement || effectiveWeekOffDays.length === 0) {
        return false; // No filtering if not regular engagement or no week-off days selected
      }

      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayNames[dayOfWeek];

      return effectiveWeekOffDays.includes(dayName);
    };

    let datesArray: Date[] = [];

    if (selectedDates && selectedDates.length > 0) {
      // Multiple dates selected - use only the explicitly selected dates (no gap filling)
      datesArray = selectedDates.sort((a, b) => a.getTime() - b.getTime());

      // Filter out week-off days
      datesArray = datesArray.filter(date => !isWeekOffDay(date));

      // Display only first and last date (after filtering)
      const displayText = datesArray.length > 1
        ? `${formatDate(datesArray[0])} - ${formatDate(datesArray[datesArray.length - 1])}`
        : datesArray.length === 1
        ? formatDate(datesArray[0])
        : 'No working days selected';

      setValue('workDates', displayText);
      handleInputChange('workDates', displayText);
    } else if (endDate) {
      // Date range selected
      const allDatesInRange: Date[] = [];
      const currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        allDatesInRange.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Filter out week-off days
      datesArray = allDatesInRange.filter(date => !isWeekOffDay(date));

      const dateRange = datesArray.length > 0
        ? `${formatDate(datesArray[0])} - ${formatDate(datesArray[datesArray.length - 1])}`
        : 'No working days selected';
      setValue('workDates', dateRange);
      handleInputChange('workDates', dateRange);
    } else {
      // Single date
      // Check if single date is a week-off day
      if (isWeekOffDay(startDate)) {
        datesArray = [];
        setValue('workDates', 'Selected date is a week-off day');
        handleInputChange('workDates', 'Selected date is a week-off day');
      } else {
        datesArray = [startDate];
        const formattedDate = formatDate(startDate);
        setValue('workDates', formattedDate);
        handleInputChange('workDates', formattedDate);
      }
    }

    setCalendarSelectedDatesByFreelancer(prev => {
      if (overrides?.appendToExisting) {
        const existing = prev[freelancerId] || [];
        const existingKeys = new Set(existing.map(d => d.toDateString()));
        const merged = [...existing, ...datesArray.filter(d => !existingKeys.has(d.toDateString()))]
          .sort((a, b) => a.getTime() - b.getTime());
        return { ...prev, [freelancerId]: merged };
      }
      return { ...prev, [freelancerId]: datesArray };
    });

    setTableRowsByFreelancer(prevRowsByFreelancer => {
      const prevRows = prevRowsByFreelancer[freelancerId] || [];
      const freelancerPricingType = overrides?.pricingType ?? lastUsedPricingTypeByFreelancer[freelancerId] ?? 'Tim';
      
      // Use override values if provided, otherwise use watch() values
      const currentHoursPerDay = overrides?.hoursPerDay ?? watch('hoursPerDayType');
      const currentHourlyPrice = overrides?.hourlyPrice ?? watch('hourlyPrice');
      const currentServiceDescription = overrides?.serviceDescription ?? serviceDescription;
      
      // Builds a fresh row for a date using the current/override values
      const buildRow = (date: Date, index: number) => {
        const formattedDate = formatDate(date);
        {
          let timeValue = '1';
          if (freelancerPricingType === 'st') {
            // Payment Per Unit: use the integer quantity from hoursPerDay
            const qty = parseInt(currentHoursPerDay || '1');
            timeValue = (!isNaN(qty) && qty > 0) ? qty.toString() : '1';
          } else if (freelancerPricingType === 'Tim') {
            const isValidHoursPerDay = currentHoursPerDay && parseFloat(currentHoursPerDay) > 0;
            if (isValidHoursPerDay) {
              // Normalize the time value (handles both 03.20 and 03:20 formats)
              if (currentHoursPerDay.includes(':')) {
                // Already in hh:mm format
                timeValue = currentHoursPerDay;
              } else if (currentHoursPerDay.includes('.')) {
                // Decimal format - check if it's HH.MM or decimal hours
                const parts = currentHoursPerDay.split('.');
                const hours = parseInt(parts[0] || '0');
                const decimal = parseInt(parts[1] || '0');
                
                if (decimal <= 59 && hours > 0) {
                  // HH.MM format (03.20 = 3 hours 20 minutes)
                  timeValue = `${hours.toString().padStart(2, '0')}:${decimal.toString().padStart(2, '0')}`;
                } else {
                  // Decimal hours format (3.75 = 3 hours 45 minutes)
                  const decimalHours = parseFloat(currentHoursPerDay);
                  const h = Math.floor(decimalHours);
                  const m = Math.round((decimalHours - h) * 60);
                  timeValue = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                }
              } else {
                // Plain number
                const hours = parseInt(currentHoursPerDay);
                timeValue = `${hours.toString().padStart(2, '0')}:00`;
              }
            } else {
              timeValue = '08:00';
            }
          }
          
          // Use currentHourlyPrice if available, otherwise default to '500'
          const priceValue = currentHourlyPrice || '500';
          
          // Use serviceDescription if not RUT/ROT deduction
          const serviceValue = isRutOrRotDeduction ? '' : currentServiceDescription;
          
          // Get article number for the pricing type
          const articleNumber = PRICING_TYPE_OPTIONS.find(opt => opt.value === freelancerPricingType)?.articleNumber || '92';
          
          return {
            id: `row-${freelancerId}-${Date.now()}-${index}`,
            date: formattedDate,
            pricingType: freelancerPricingType,
            pricingTypeArticleNumber: articleNumber,
            service: serviceValue,
            customWorkingDescription: serviceValue, // Set custom description for non-RUT/ROT
            time: timeValue,
            price: priceValue,
            discount: { value: 0, type: 'percentage' as const },
            subtotal: 0
          };
        }
      };

      // Append mode: preserve every existing row and only add dates that aren't
      // already present, so previously added working hours are never dropped.
      if (overrides?.appendToExisting) {
        const existingDateKeys = new Set(prevRows.map(row => row.date));
        const rowsToAppend = datesArray
          .filter(date => !existingDateKeys.has(formatDate(date)))
          .map((date, index) => buildRow(date, index));

        return {
          ...prevRowsByFreelancer,
          [freelancerId]: [...prevRows, ...rowsToAppend]
        };
      }

      // Default (calendar) mode: the current selection is the source of truth.
      // Reuse existing rows for still-selected dates, drop the rest.
      const newTableRows = datesArray.map((date, index) => {
        const formattedDate = formatDate(date);
        const existingRow = prevRows.find(row => row.date === formattedDate);
        return existingRow ? existingRow : buildRow(date, index);
      });

      const updated = {
        ...prevRowsByFreelancer,
        [freelancerId]: newTableRows
      };

      return updated;
    });

    // Only close calendar if it's single date selection (not multiple)
    if (!selectedDates) {
      setShowCalendar(false);
    }
  };

  return {
    handleDateSelect
  };
};
